import { QuestionDifficulty } from "@/enums/contentTypes";
import {
  Card,
  SimpleGrid,
  Stack,
  Text,
  Title,
  TitleOrder,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import FlagRounded from "@mui/icons-material/FlagRounded";
import MergeTypeRounded from "@mui/icons-material/MergeTypeRounded";
import QuestionMarkRounded from "@mui/icons-material/QuestionMarkRounded";
import { SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { capFirst } from "../../../../../../../../../lib/strapi/formatters";
import classes from "./TestPreviewStep.module.css";
import { LimitType, MockExamType, TestFormats } from "./types";

interface StepPreviewProps {
  subjectNameLower: string;
  testType: TestFormats.custom | MockExamType;
  numSelectedTopics: number;
  numSelectedSubTopics: number;
  selectedDifficulties: QuestionDifficulty[];
  limitType: LimitType;
  limitValue: number;
  mcAbsoluteProportion: number;
}

export const useSummaryTileStyles = () => ({ classes });

export default function TestPreviewStep({
  subjectNameLower,
  testType,
  numSelectedTopics,
  numSelectedSubTopics,
  selectedDifficulties,
  limitType,
  limitValue,
  mcAbsoluteProportion,
}: StepPreviewProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");
  const titleProps = {
    order: 5 as TitleOrder,
    c: colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.dark[4],
    mb: "1rem",
  };

  return (
    <div>
      <Title {...titleProps}>
        3) Review {testType === TestFormats.custom ? "Test" : "Mock Exam"}
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <SummaryTile
          Icon={QuestionMarkRounded}
          label="Selected"
          value={`${numSelectedTopics} topics, ${numSelectedSubTopics} subtopics`}
        />

        <SummaryTile
          Icon={FlagRounded}
          label="Difficulties"
          value={
            selectedDifficulties.length === 3
              ? "All difficulties"
              : selectedDifficulties.length > 0
              ? selectedDifficulties.map(capFirst).join(", ")
              : "Not set"
          }
        />

        <SummaryTile
          Icon={MergeTypeRounded}
          label={`${
            limitType === "questions" ? "Question" : "Time"
          } proportion`}
          value={formatMcLaSummary(limitType, limitValue, mcAbsoluteProportion)}
        />
      </SimpleGrid>
    </div>
  );
}

export function SummaryTile({
  Icon,
  label,
  value,
}: {
  Icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string };
  value: string;
  label?: string;
}) {
  return (
    <Card className={classes.card}>
      <Icon className={classes.icon} />
      <Stack className={classes.content} gap="xs" align="flex-start">
        {label && <Text className={classes.label}>{label}</Text>}
        <Text className={classes.value}>{value}</Text>
      </Stack>
    </Card>
  );
}

function formatMcLaSummary(
  limitType: LimitType,
  limitValue: number,
  mcAbsoluteProportion: number
) {
  const unit = limitType === "questions" ? "questions" : "minutes";
  const la = limitValue - mcAbsoluteProportion;
  return `MC ${mcAbsoluteProportion} ${unit} • LA ${la} ${unit}`;
}

export function convertTestTypeToNiceName(
  testType: TestFormats.custom | MockExamType
) {
  switch (testType) {
    case "slMcPaper":
      return "SL Paper 1";
    case "hlMcPaper":
      return "HL Paper 1";
    case "slLaPaper":
      return "SL Paper 2";
    case "hlLaPaper":
      return "HL Paper 2";
    default:
      return "Custom Test";
  }
}
